import React, { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { EXTENSION_ROUTES } from '../config/navigation';
import { useAuth } from '../context/AuthContext';
import {
  AbuseTimelinePage,
  AccountSettings,
  AffiliatorReportPage,
  CasinoDashboardPage,
  CorrectionReportPage,
  DashboardPage,
  DiscountReportPage,
  PaymentCommissionPage,
  KasaPage,
  WithdrawOpsPage,
  OzelOranPage,
  ExtensionPlaceholderPage,
  FaturaPage,
  FinancialPage,
  FraudRiskPage,
  CustomerRiskPage,
  CustomerRiskProfilePage,
  FreeSpinDashboardPage,
  PlayerBonusesPage,
  FtdReportPage,
  GeriDonenlerPage,
  BirthdaysPage,
  GgrGamePage,
  GgrPlayerCasinoPage,
  GgrProviderPage,
  IpAnalysisPage,
  MemberTrackingPage,
  MonthlyGrowthPage,
  CohortRetentionPage,
  SportsbookReportPage,
  TopLosersPage,
  TopWinnersPage,
  TelegramSettingsPage,
  AiSettingsPage,
  UserManagementPage,
  RoleManagementPage,
  UploadPage,
} from '../lazyPages';
import { MobileTopbar } from './MobileTopbar';
import { PageLoader } from './ui/PageLoader';
import { PageShell } from './ui/PageShell';
import { Sidebar } from './Sidebar';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const main = document.querySelector('.main');
    if (main) main.scrollTop = 0;
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ProtectedRoute({ pathSlug, children }) {
  const { canAccessRoute } = useAuth();
  if (!canAccessRoute(pathSlug)) {
    return (
      <PageShell>
        <div className="alert alert-danger" style={{ margin: '24px' }}>
          <p>
            <strong>Yetkisiz erişim.</strong> Bu sayfayı görüntüleme izniniz yok. Menüden erişebildiğiniz
            sayfalara dönebilirsiniz.
          </p>
        </div>
      </PageShell>
    );
  }
  return children;
}

function LazyRoute({ pathSlug, element: Element, title }) {
  return (
    <ProtectedRoute pathSlug={pathSlug}>
      <Suspense fallback={<PageLoader label={title ? `${title} yükleniyor…` : 'Sayfa yükleniyor…'} />}>
        <Element />
      </Suspense>
    </ProtectedRoute>
  );
}

function AccountSettingsRoute({ onAuthRefresh }) {
  return (
    <ProtectedRoute pathSlug="hesap">
      <Suspense fallback={<PageLoader label="Hesap yükleniyor…" />}>
        <AccountSettings onAuthRefresh={onAuthRefresh} />
      </Suspense>
    </ProtectedRoute>
  );
}

export function AppLayout({ onLogout, onAuthRefresh }) {
  const location = useLocation();
  const { username } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('sidebar-open', sidebarOpen);
    return () => document.body.classList.remove('sidebar-open');
  }, [sidebarOpen]);

  return (
    <div className="app">
      <button
        type="button"
        className={`sidebar-backdrop${sidebarOpen ? ' sidebar-backdrop--visible' : ''}`}
        aria-label="Menüyü kapat"
        onClick={() => setSidebarOpen(false)}
        tabIndex={sidebarOpen ? 0 : -1}
      />
      <Sidebar authUsername={username} onLogout={onLogout} open={sidebarOpen} onNavigate={() => setSidebarOpen(false)} />
      <div className="main">
        <MobileTopbar pathname={location.pathname} onMenuToggle={() => setSidebarOpen((v) => !v)} />
        <ScrollToTop />
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<LazyRoute pathSlug="dashboard" element={DashboardPage} title="Dashboard" />} />
          <Route path="financial" element={<LazyRoute pathSlug="financial" element={FinancialPage} title="Finansal İşlemler" />} />
          <Route path="ftd-report" element={<LazyRoute pathSlug="ftd-report" element={FtdReportPage} title="FTD Raporu" />} />
          <Route path="affiliator" element={<LazyRoute pathSlug="affiliator" element={AffiliatorReportPage} title="Affiliator Raporu" />} />
          <Route path="top-winners" element={<LazyRoute pathSlug="top-winners" element={TopWinnersPage} title="En İyi Yatırımcılar" />} />
          <Route path="ip-analysis" element={<LazyRoute pathSlug="ip-analysis" element={IpAnalysisPage} title="IP Analizi" />} />
          <Route path="top-losers" element={<LazyRoute pathSlug="top-losers" element={TopLosersPage} title="Artıdaki Üyeler" />} />
          <Route path="geri-donenler" element={<LazyRoute pathSlug="geri-donenler" element={GeriDonenlerPage} title="Geri Dönenler" />} />
          <Route path="hesap" element={<AccountSettingsRoute onAuthRefresh={onAuthRefresh} />} />
          <Route path="kullanicilar" element={<LazyRoute pathSlug="kullanicilar" element={UserManagementPage} title="Kullanıcılar" />} />
          <Route path="roller" element={<LazyRoute pathSlug="roller" element={RoleManagementPage} title="Roller" />} />
          <Route path="telegram" element={<LazyRoute pathSlug="telegram" element={TelegramSettingsPage} title="Telegram" />} />
          <Route path="yapay-zeka" element={<LazyRoute pathSlug="yapay-zeka" element={AiSettingsPage} title="Rapor Analizi" />} />
          <Route path="upload" element={<LazyRoute pathSlug="upload" element={UploadPage} title="Rapor Yükle" />} />
          <Route path="monthly-growth" element={<LazyRoute pathSlug="monthly-growth" element={MonthlyGrowthPage} title="Aylık Büyüme" />} />
          <Route path="cohort-retention" element={<LazyRoute pathSlug="cohort-retention" element={CohortRetentionPage} title="Cohort / Retention" />} />
          <Route path="casino-dashboard" element={<LazyRoute pathSlug="casino-dashboard" element={CasinoDashboardPage} title="Casino Dashboard" />} />
          <Route path="sportsbook" element={<LazyRoute pathSlug="sportsbook" element={SportsbookReportPage} title="Sportsbook" />} />
          <Route path="free-spin" element={<LazyRoute pathSlug="free-spin" element={FreeSpinDashboardPage} title="Free Spin" />} />
          <Route path="bonus-raporu" element={<LazyRoute pathSlug="bonus-raporu" element={PlayerBonusesPage} title="Bonus Raporu" />} />
          <Route path="uye-takip" element={<LazyRoute pathSlug="uye-takip" element={MemberTrackingPage} title="Üye Takip" />} />
          <Route path="dogum-gunleri" element={<LazyRoute pathSlug="dogum-gunleri" element={BirthdaysPage} title="Doğum Günleri" />} />
          <Route path="suistimal" element={<LazyRoute pathSlug="suistimal" element={AbuseTimelinePage} title="Suistimal" />} />
          <Route path="fraud-risk" element={<LazyRoute pathSlug="fraud-risk" element={FraudRiskPage} title="Fraud Risk" />} />
          <Route
            path="musteri-risk/uye/:username"
            element={<LazyRoute pathSlug="musteri-risk" element={CustomerRiskProfilePage} title="Üye Risk Profili" />}
          />
          <Route path="musteri-risk" element={<LazyRoute pathSlug="musteri-risk" element={CustomerRiskPage} title="Müşteri Risk" />} />
          <Route path="ggr-oyuncu" element={<LazyRoute pathSlug="ggr-oyuncu" element={GgrPlayerCasinoPage} title="GGR / Oyuncu" />} />
          <Route path="ggr-saglayici" element={<LazyRoute pathSlug="ggr-saglayici" element={GgrProviderPage} title="GGR / Sağlayıcı" />} />
          <Route path="fatura" element={<LazyRoute pathSlug="fatura" element={FaturaPage} title="Fatura" />} />
          <Route path="ggr-oyun" element={<LazyRoute pathSlug="ggr-oyun" element={GgrGamePage} title="GGR / Oyun" />} />
          <Route path="discount-raporu" element={<LazyRoute pathSlug="discount-raporu" element={DiscountReportPage} title="Discount Raporu" />} />
          <Route path="yatirim-cekim-raporu" element={<LazyRoute pathSlug="yatirim-cekim-raporu" element={PaymentCommissionPage} title="Yatırım Çekim Raporu" />} />
          <Route path="kasa" element={<LazyRoute pathSlug="kasa" element={KasaPage} title="Kasa" />} />
          <Route path="cekim-operasyon" element={<LazyRoute pathSlug="cekim-operasyon" element={WithdrawOpsPage} title="Çekim Operasyon" />} />
          <Route path="ozel-oran" element={<LazyRoute pathSlug="ozel-oran" element={OzelOranPage} title="Özel Oran" />} />
          <Route path="correction-raporu" element={<LazyRoute pathSlug="correction-raporu" element={CorrectionReportPage} title="Correction Raporu" />} />
          {EXTENSION_ROUTES.map(({ path, title, description }) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute pathSlug={path}>
                  <Suspense fallback={<PageLoader label={`${title} yükleniyor…`} />}>
                    <ExtensionPlaceholderPage title={title} />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          ))}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </div>
  );
}
